import { ChangeDetectorRef, Component, makeStateKey, OnInit, TransferState } from "@angular/core";
import { MetaDefinition } from "@angular/platform-browser";
import { RouterLink } from "@angular/router";
import { ListaBlogs } from "@src/app/models/interfaces/blog.interface";
import { BlogService } from "@src/app/servicios/blog/blog.service";
import { FooterService } from "@src/app/servicios/footer.service";
import { firstValueFrom } from "rxjs";
import { ConfigService } from "src/app/servicios/config/config.service";
import { GfkService } from "src/app/servicios/gfk/gfk.service";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { TitleWrapperService } from "src/app/servicios/title-wrapper/title-wrapper.service";
import { SkeletonImageComponent } from "../skeleton-image/skeleton-image/skeleton-image.component";
import { CanonicalService } from "@src/app/servicios/canonical/canonical.service";

const POST_KEY = makeStateKey<any>("listado-blogs");

@Component({
  selector: "app-vista-blogs",
  templateUrl: "./vista-blogs.component.html",
  styleUrls: ["./vista-blogs.component.css"],
  imports: [RouterLink, SkeletonImageComponent],
  providers: [BlogService],
})
export class VistaBlogsComponent implements OnInit {
  constantesGlobales: any;
  blogs: ListaBlogs[] = [];
  urlBlog: any;

  constructor(
    private readonly blogService: BlogService,
    private readonly gfkService: GfkService,
    private readonly titleWrapperService: TitleWrapperService,
    private readonly platformService: PlatformService,
    private readonly configService: ConfigService,
    private readonly transferState: TransferState,
    private readonly cdr: ChangeDetectorRef,
    private readonly canonicalService: CanonicalService,
    private readonly footerService: FooterService,
  ) {}

  ngOnInit(): void {
    this.footerService.isPaginationFinished.set(true);
    this.constantesGlobales = this.configService.getConstantesGlobales();
    this.titleWrapperService.setTitle("Blogs - " + this.constantesGlobales.siteName);
    this.getBlogList();
    if (this.constantesGlobales.gfk === true) {
      if (this.platformService.isBrowser()) {
        this.initGfk();
      }
    }
  }

  /**
   * Verifica si la plataforma es servidor (SSR) o no hay ninguna publicación en el TransferState de la aplicación, si es
   * el caso se recupera la lista de Blogs y se almacena el estado, si no, se recupera el estado TransferState.
   */
  getBlogList(): void {
    if (!this.platformService.isBrowser()) {
      this.fetchBlogsAndStoreKey();
    } else {
      if (this.transferState.hasKey(POST_KEY)) {
        this.blogs = this.transferState.get<ListaBlogs[]>(POST_KEY, null);
        this.transferState.remove(POST_KEY);
      } else {
        this.fetchBlogsAndStoreKey();
      }
    }
  }

  /**
   * Método asíncrono que obtiene una lista de blogs desde el servicio `blogService`,
   * actualiza las etiquetas meta, almacena la lista en el estado de transferencia
   * y solicita la detección de cambios en el componente.
   *
   * @returns {Promise<void>} Una promesa que se resuelve cuando se completa la operación.
   *
   * @remarks
   * - Utiliza `firstValueFrom` para convertir el observable devuelto por `getListadoBlogs`
   *   en una promesa.
   * - La lista de blogs se almacena en la propiedad `blogs` del componente.
   * - Las etiquetas meta se actualizan llamando al método `updateMetaTags`.
   * - La lista de blogs se almacena en el estado de transferencia utilizando la clave `POST_KEY`.
   * - Se llama a `cdr.detectChanges()` para forzar la detección de cambios en el componente.
   *
   * @throws {Error} Si ocurre un error al obtener la lista de blogs o al procesar los datos.
   */
  async fetchBlogsAndStoreKey(): Promise<void> {
    const listBlog = this.blogService.getListadoBlogs(this.constantesGlobales.idAmbito);
    if (listBlog) {
      const listaBlogs: ListaBlogs[] = await firstValueFrom(listBlog);
      this.blogs = listaBlogs;
      this.updateMetaTags();
      this.transferState.set(POST_KEY, listaBlogs);
      this.cdr.detectChanges();
    }
  }

  /**
   * Actualiza las metaetiquetas de la página con información relevante del post actual.
   * Genera un array de metaetiquetas que incluye datos como descripción, título, autor,
   * editor, y otros metadatos relacionados con el contenido del post.
   *
   * Las metaetiquetas generadas incluyen tanto etiquetas estándar como etiquetas específicas
   * para Open Graph (og) y Dublin Core (DC).
   *
   * @private
   */
  updateMetaTags(): void {
    const metaTagsArray: MetaDefinition[] = [
      { property: "og:title", content: this.constantesGlobales.siteTitle },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${this.constantesGlobales.siteDomain}/assets/images/${this.constantesGlobales.imgShare}` },
      { property: "og:url", content: this.constantesGlobales.siteDomain },
      { property: "og:description", content: this.constantesGlobales.metaTag },
      { property: "article:author", content: this.constantesGlobales.siteName },
      { property: "article:publisher", content: this.constantesGlobales.siteName },
      { property: "og:site_name", content: this.constantesGlobales.siteDomain },
      { name: "DC.title", content: this.constantesGlobales.siteTitle },
      { name: "DC.creator", content: this.constantesGlobales.siteTitle },
      { name: "DC.publisher", content: this.constantesGlobales.siteTitle },
      { name: "DC.description", content: this.constantesGlobales.metaTag },
      { name: "DC.subject", content: this.constantesGlobales.metaTag },
      { name: "DC.date", content: new Date() },
    ];

    this.canonicalService.updateCanonicalUrls(metaTagsArray);
  }

  private initGfk(): void {
    if (this.constantesGlobales.gfk) {
      const customParams = { c1: this.stripProtocolAndSubdomain(this.constantesGlobales.siteDomain), c2: "blogs" };
      this.gfkService.impression(customParams);
    }
  }

  stripProtocolAndSubdomain(url: string): string {
    const strippedUrl = url.replace(/^(https?:\/\/)?(www\.)?/, "");
    return strippedUrl;
  }
}
